import {computed} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {patchState, signalStore, withComputed, withHooks, withMethods, withState} from '@ngrx/signals';
import {rxMethod} from '@ngrx/signals/rxjs-interop';
import {defer, EMPTY, fromEvent, map, merge, pipe, retry, switchMap, tap, timer} from 'rxjs';
import {environment} from 'src/environments/environment';

/** What the connection banner shows: no network, an engine that does not answer, or the connection just back. */
export type ConnectionIssue = 'offline' | 'unreachable' | 'restored';

interface ConnectionState {
	offline: boolean;
	unreachable: boolean;
	/** A probe of the engine is on its way. */
	probing: boolean;
	/** When the next probe runs, for the banner's countdown. */
	nextProbeAt: number | null;
	restored: boolean;
}

export const FIRST_PROBE_DELAY_MS = 2_000;
const MAX_PROBE_DELAY_MS = 30_000;
const PROBE_TIMEOUT_MS = 5_000;
export const RESTORED_DISPLAY_MS = 3_000;

/** Resolves when the engine answers its public liveness route; fails without an answer, or on a gateway error. */
function ping(): Promise<void> {
	return fetch(`${environment.engineURL}/liveness`, {cache: 'no-store', signal: AbortSignal.timeout(PROBE_TIMEOUT_MS)})
		.then(response => {
			if (response.status >= 500) {
				throw new Error(`HTTP ${response.status}`);
			}
		});
}

/**
 * Whether the app can reach the engine, for the banner at the top of the app (KAB-2441): the browser says when the
 * network goes, `connectionInterceptor` when a request to the engine gets no answer. One lost request is not an outage:
 * the engine is probed first, and only a probe without an answer shows the banner, then again on a growing delay until
 * it answers. The screens show their own errors meanwhile; this only says why.
 */
export const ConnectionStore = signalStore(
	{providedIn: 'root'},
	withState<ConnectionState>({offline: !navigator.onLine, unreachable: false, probing: false, nextProbeAt: null, restored: false}),
	withComputed(({offline, unreachable, restored}) => ({
		issue: computed<ConnectionIssue | null>(() => offline() ? 'offline' : unreachable() ? 'unreachable' : restored() ? 'restored' : null),
	})),
	withMethods(store => {
		/** A probing loop runs, or the network just came back: an answer then says the connection is restored. */
		let recovering = false;

		const showRestored = rxMethod<void>(pipe(
			tap(() => patchState(store, {restored: true})),
			switchMap(() => timer(RESTORED_DISPLAY_MS)),
			tap(() => patchState(store, {restored: false})),
		));

		const reachable = (): void => {
			const wasDown = store.unreachable() || recovering;
			recovering = false;
			patchState(store, {unreachable: false, probing: false, nextProbeAt: null});
			if (wasDown) {
				showRestored();
			}
		};

		// One loop at a time: a delay (re)starts it, null stops it.
		const probe = rxMethod<number | null>(pipe(
			switchMap(delay => delay === null ? EMPTY : timer(delay).pipe(
				switchMap(() => defer(() => {
					patchState(store, {probing: true, nextProbeAt: null});
					return ping();
				})),
				retry({
					delay: (_, attempt) => {
						const wait = Math.min(MAX_PROBE_DELAY_MS, FIRST_PROBE_DELAY_MS * 2 ** (attempt - 1));
						patchState(store, {unreachable: true, probing: false, nextProbeAt: Date.now() + wait});
						return timer(wait);
					},
				}),
				tap(() => reachable()),
			)),
		));

		const lost = (): void => {
			// A probe already checks: a failed request each second must not reset its delay.
			if (store.offline() || store.unreachable() || store.probing()) {
				return;
			}
			probe(0);
		};

		return {
			/** A request to the engine got no answer. */
			noAnswer: lost,
			/** The engine answered a request, whatever its status: it is reachable. */
			answered(): void {
				if (store.unreachable() || store.probing()) {
					probe(null);
					reachable();
				}
			},
			/** The close cross of "Connexion rétablie". */
			dismissRestored(): void {
				patchState(store, {restored: false});
			},
			/** "Réessayer" in the banner: probes now. */
			retryNow(): void {
				if (!store.offline() && !store.probing()) {
					probe(0);
				}
			},
			_networkChanged(online: boolean): void {
				if (online) {
					// The browser has a network again, which does not mean the engine answers: probe it.
					recovering = true;
					patchState(store, {offline: false});
					probe(0);
				} else {
					probe(null);
					patchState(store, {offline: true, unreachable: false, probing: false, nextProbeAt: null, restored: false});
				}
			},
		};
	}),
	withHooks({
		onInit(store) {
			merge(fromEvent(window, 'online').pipe(map(() => true)), fromEvent(window, 'offline').pipe(map(() => false)))
				.pipe(takeUntilDestroyed())
				.subscribe(online => store._networkChanged(online));
		},
	}),
);
